import { isNullish } from '@sas/nova-core/typeguards';
import { createUseTextLocalizer } from '../../../../../locale/createUseTextLocalizer';
import { useIconA11yInfo } from '../../../../Icon/useIconA11yInfo';
import { isIconColumn } from '../../utils/typeguards';
import { getCellHelpPopup, getCellIcon, getCellMenuButton } from '../../utils/utils';
import { ROLE_TEMPLATE_LITERAL } from './types';
import avatarBundle from '../../../../Avatar/NovaComponents-Avatar-gui-icu.properties';
const useAvatarTextLocalizer = createUseTextLocalizer(avatarBundle);

/**
 * @internal
 */

/**
 * @internal
 */
export const useGetInfoCell = props => {
  const {
    getItemType,
    getItemProps,
    getRowKey,
    helpPopupButtonRefs,
    menuButtonRefs,
    accessibilityInfo,
    readOnly,
    isItemReadOnly
  } = props;
  const getIconA11yInfo = useIconA11yInfo();
  const avatarLocalize = useAvatarTextLocalizer();
  return cellProps => {
    const {
      rowIndex,
      column
    } = cellProps;
    const rowKey = getRowKey?.(rowIndex);
    const readOnlyRow = !!readOnly || !!(rowKey && isItemReadOnly?.(rowKey));
    const iconColumn = column && isIconColumn(column) ? column : undefined;

    // give consumer an option to completely replace the cell info
    if (typeof accessibilityInfo?.getCellInfo === 'function') {
      const cellInfo = accessibilityInfo.getCellInfo(cellProps);
      if (cellInfo) return cellInfo;
    }
    const itemType = getItemType(rowIndex);
    const accProps = getItemProps(rowIndex);
    if (itemType === 'groupItem') {
      return makeGroupItemCellInfo(accProps);
    } else if (!iconColumn) {
      if (accProps) {
        if (itemType === 'item') return makeItemCellInfo(accProps, getIconA11yInfo, avatarLocalize);else if (itemType === 'linkItem') return makeLinkItemCellInfo(accProps, getIconA11yInfo, readOnlyRow);
      }
    } else if (iconColumn) {
      const cellHelpPopup = getCellHelpPopup(iconColumn, rowIndex);
      const cellMenuButton = getCellMenuButton(iconColumn, rowIndex);
      menuButtonRefs;
      const itemDescription = iconColumn.itemDescription?.(rowIndex);
      const associatedItemDesc = itemDescription ? `, ${itemDescription}` : itemType === 'item' ? `, ${accProps?.title}` : itemType === 'linkItem' && typeof accProps.children === 'string' ? `, ${accProps.children}` : '';
      if (cellMenuButton && menuButtonRefs) {
        const iconInfo = makeItemIconMenuButtonInfo(menuButtonRefs[rowIndex]);
        if (iconInfo) return `${iconInfo}${associatedItemDesc}`;
      }
      if (cellHelpPopup) {
        if (helpPopupButtonRefs && (readOnlyRow && iconColumn?.displayHelpPopupReadOnly?.(rowIndex) || !readOnlyRow)) {
          const iconInfo = makeItemIconHelpPopupInfo(helpPopupButtonRefs[rowIndex]);
          if (iconInfo) return `${iconInfo}${associatedItemDesc}`;
        }
      } else if (!iconColumn?.onPress || !readOnlyRow) {
        const iconInfo = makeItemIconCellInfo(iconColumn, getIconA11yInfo, rowIndex);
        if (iconInfo) return `${iconInfo}${associatedItemDesc}`;
      }
    }
    return null;
  };
};

/**
 * @internal
 */
const makeItemCellInfo = (props, getIconA11yInfo, avatarLocalize) => {
  const getIconDescription = icon => {
    const {
      ariaLabel,
      tooltip
    } = getIconA11yInfo(icon);
    return ariaLabel ?? tooltip;
  };
  const text = [];
  if (props.icon && !props.announceIconLast) {
    text.push(getIconDescription(props.icon));
  }
  if (props.title) {
    text.push(props.title);
  }
  if (props.description) {
    text.push(props.description);
  }
  if (props.info) {
    if (props.info.concatInfo) text.push('-');
    text.push(props.info.infoText);
  }
  if (!isNullish(props.counter)) {
    text.push(`(${props.counter})`);
  }
  if (props.icon && props.announceIconLast) {
    text.push(getIconDescription(props.icon));
  }
  if (props.tooltip) {
    text.push(`, ${props.tooltip}`);
  }
  const info = text.length > 0 ? text.join(', ').replace(/, -,/g, ' -') : null;
  if (info && !props.icon && props.avatar) {
    return props.avatar.ariaLabel ? [info, props.avatar.ariaLabel].join(', ') : avatarLocalize(`avatar.${props.avatar.type ?? 'user'}.tooltip.label`, {
      name: info
    });
  }
  return info;
};

/**
 * @internal
 */
const makeItemIconHelpPopupInfo = helpPopupButtonRef => helpPopupButtonRef?.ariaLabel ?? helpPopupButtonRef?.title;

/**
 * @internal
 */
const makeItemIconMenuButtonInfo = menuButtonRef => menuButtonRef?.ariaLabel ?? menuButtonRef?.title;

/**
 * @internal
 */
const makeItemIconCellInfo = (column, getIconA11yInfo, rowIndex) => {
  const cellIcon = getCellIcon(column, rowIndex);
  // note that final aria-label, including tooltip, is handled at getIconA11yInfo
  const ariaLabel = cellIcon ? getIconA11yInfo(cellIcon).ariaLabel : null;
  return ariaLabel;
};

/**
 * @internal
 */
const makeLinkItemCellInfo = (props, getIconA11yInfo, readOnlyRow) => {
  const text = [];
  const getIconDescription = icon => {
    const {
      ariaLabel,
      tooltip
    } = getIconA11yInfo(icon);
    return ariaLabel ?? tooltip;
  };
  if (props.icon && !props.announceIconLast) {
    text.push(getIconDescription(props.icon));
  }
  text.push(props.children);
  if (props.description) {
    if (!readOnlyRow) {
      text.push(`${ROLE_TEMPLATE_LITERAL}, ${props.description}`);
    } else {
      text.push(props.description);
    }
  }
  if (props.icon && props.announceIconLast) {
    text.push(getIconDescription(props.icon));
  }

  // Link's title is tooltip
  if (props.title) {
    text.push(`, ${props.title}`);
  }
  return text.length > 0 ? text.join(', ') : null;
};

/**
 * @internal
 */
const makeGroupItemCellInfo = props => props.title;
//# sourceMappingURL=useGetInfoCell.js.map